import type { ReactNode } from 'react'
import type { DiscountRuleType } from '@/lib/api/discount-types'
import { cn } from '@/lib/utils'
import {
  amountUnitLabel,
  currencyEquivalent,
  parseDecimal,
  RULE_LABEL,
  type MenuUnits,
  type RuleDraft,
} from './editor-model'
import { HINT, fieldClass } from './editor-styles'

export function Section({ title, description, aside, children }: {
  title: string
  description?: ReactNode
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3 border-t border-neutral-200 pt-5 first:border-t-0 first:pt-0 dark:border-neutral-800">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">{title}</h3>
          {description && <p className={cn(HINT, 'mt-0.5')}>{description}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  )
}

export function FieldError({ children }: { children: ReactNode }) {
  if (!children) return null
  return <p className="mt-1 text-xs text-red-600 dark:text-red-400">{children}</p>
}

export function Notice({ kind, children }: { kind: 'error' | 'info' | 'warning' | 'success'; children: ReactNode }) {
  const tone = {
    error: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300',
    info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
    warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200',
    success: 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200',
  }[kind]
  return <div role={kind === 'error' ? 'alert' : 'status'} className={cn('rounded-lg border p-3 text-sm', tone)}>{children}</div>
}

function ruleHelp(type: DiscountRuleType, units: MenuUnits): string {
  if (type === 'set_price') return "Charge exactly this for each item. It never raises a price: an item listed below it keeps its list price."
  if (type === 'tokens_off') return units.tokenEvent ? "Take this many tokens off each item's list price." : "Take this much off each item's list price."
  return "Take this percent off each item's list price."
}

/**
 * A rule: its type, and a value in menu units (the same numbers the menu
 * screen's price field takes) or a percent. Switching between an amount and
 * a percent clears the value, since the units differ.
 */
export function RuleInput({ id, rule, onChange, units, error, allowNone, noneLabel, compact, subject }: {
  id: string
  rule: RuleDraft
  onChange: (rule: RuleDraft) => void
  units: MenuUnits
  error?: string | null
  /** What the rule prices (an item's name), so each of several rules on a page has its own control labels. */
  subject?: string
  /** Offer "no rule" (the default rule of an overrides-only schedule). */
  allowNone?: boolean
  noneLabel?: string
  /** Leave out the explanation under the field. */
  compact?: boolean
}) {
  // Percent off is not offered on token events; a rule saved before the
  // event became one still shows so it can be read and replaced.
  const allowPercent = !units.tokenEvent || rule.type === 'percent_off'
  const parsed = parseDecimal(rule.value)
  const equivalent = rule.type && rule.type !== 'percent_off' && parsed ? currencyEquivalent(parsed.value, units, rule.type) : null
  const changeType = (type: RuleDraft['type']) => {
    const samePercentness = (type === 'percent_off') === (rule.type === 'percent_off')
    onChange({ type, value: type && samePercentness ? rule.value : '' })
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id={`${id}-type`}
          aria-label={subject ? `Rule type for ${subject}` : 'Rule type'}
          value={rule.type}
          onChange={(e) => changeType(e.target.value as RuleDraft['type'])}
          className={fieldClass(!!error && !rule.type, 'w-40')}
        >
          {allowNone && <option value="">{noneLabel ?? 'No rule'}</option>}
          <option value="set_price">{RULE_LABEL.set_price}</option>
          <option value="tokens_off">{RULE_LABEL.tokens_off}</option>
          {allowPercent && <option value="percent_off">{RULE_LABEL.percent_off}</option>}
        </select>
        {rule.type && (
          <div className="flex items-center gap-2">
            <input
              id={id}
              aria-label={`${rule.type === 'percent_off' ? 'Percent' : `Amount in ${amountUnitLabel(units)}`}${subject ? ` for ${subject}` : ''}`}
              inputMode={units.tokenEvent && rule.type !== 'percent_off' ? 'numeric' : 'decimal'}
              autoComplete="off"
              value={rule.value}
              placeholder={rule.type === 'percent_off' ? '15' : units.tokenEvent ? '2' : '2.50'}
              onChange={(e) => onChange({ type: rule.type, value: e.target.value })}
              className={fieldClass(!!error, 'w-28 tabular-nums')}
            />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">
              {rule.type === 'percent_off' ? '%' : amountUnitLabel(units)}
            </span>
            {equivalent && <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">= {equivalent}</span>}
          </div>
        )}
      </div>
      {error ? <FieldError>{error}</FieldError> : !compact && rule.type ? <p className={cn(HINT, 'mt-1')}>{ruleHelp(rule.type, units)}</p> : null}
    </div>
  )
}
